/**
 * The map's `// schedule:` lines as a table, and the table back as lines.
 *
 * The file stays the source of truth, as it is for every other directive: the
 * table is *derived* from the text on every render, so a hand edit in the
 * textarea re-derives the rows, and an edit in the table writes schedule lines
 * back into the text and touches nothing else — not the grid, not the other
 * directives, not even the spacing of a schedule line nobody changed. Whether
 * a schedule is *valid* (a known dish, inside the round, never more open than
 * the rail holds) is the engine's answer in the checklist, not this module's:
 * this only reads and writes the lines.
 *
 *   // schedule: 0s fried_meat, fried_meat     one row, two tickets at 0:00
 *   // schedule: 60s veg_stew                  another row
 */

/** One `// schedule:` line. */
export interface ScheduleRow {
  /**
   * The time as written, without the unit — `"0"`, `"12.5"` — or null when the
   * line does not read as a time. An unreadable line is still a row, so it can
   * be seen and removed; the checklist says what is wrong with it.
   */
  seconds: string | null
  /** The dishes on the line, in file order. Each is one ticket. */
  dishes: string[]
  /** The line exactly as it stands in the file (rebuilt when the row changes). */
  line: string
}

const DIRECTIVE = /^\s*\/\/\s*schedule\s*:(.*)$/i
const ENTRY = /^\s*(\d+(?:\.\d+)?)s\s+(\S.*)$/
/** What the time box accepts: the same number the directive does, without its `s`. */
export const SECONDS = /^\d+(?:\.\d+)?$/

const isSchedule = (line: string): boolean => DIRECTIVE.test(line)

function readRow(line: string): ScheduleRow {
  const rest = DIRECTIVE.exec(line)?.[1] ?? ''
  const entry = ENTRY.exec(rest)
  if (!entry) return { seconds: null, dishes: [], line }
  const dishes = entry[2]
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d.length > 0)
  return { seconds: entry[1], dishes, line }
}

/** The line a row writes: the canonical form the engine's serialiser writes too. */
export function scheduleLine(seconds: string, dishes: readonly string[]): string {
  return `// schedule: ${seconds}s ${dishes.join(', ')}`
}

/** Every schedule line in the file, in file order. */
export function scheduleRows(text: string): ScheduleRow[] {
  return text.split('\n').filter(isSchedule).map(readRow)
}

/**
 * The file with its schedule lines replaced by `rows`, and every other line
 * left exactly as it was.
 *
 * Row `i` takes the place of the file's `i`-th schedule line. Rows past the
 * file's last schedule line go in after it — or, in a file with none yet,
 * after the leading directives (so the file still reads directives first,
 * then the picture). Schedule lines past the last row are dropped.
 */
export function writeSchedule(text: string, rows: readonly ScheduleRow[]): string {
  const lines = text.split('\n')
  const at = lines.flatMap((line, i) => (isSchedule(line) ? [i] : []))
  const out: string[] = []
  let anchor = -1
  if (at.length === 0) {
    // After the last comment line that comes before the grid; else the top.
    for (let i = 0; i < lines.length; i += 1) {
      const trimmed = lines[i].trim()
      if (trimmed.startsWith('//')) anchor = i
      else if (trimmed.length > 0) break
    }
  } else {
    anchor = at[at.length - 1]
  }
  const extra = rows.slice(at.length).map((row) => row.line)
  if (anchor < 0) out.push(...extra)
  let next = 0
  lines.forEach((line, i) => {
    if (isSchedule(line)) {
      if (next < rows.length) out.push(rows[next].line)
      next += 1
    } else {
      out.push(line)
    }
    if (i === anchor) out.push(...extra)
  })
  return out.join('\n')
}

const sameTime = (a: string | null, b: string): boolean => a !== null && Number(a) === Number(b)

/**
 * Add one ticket at `seconds`.
 *
 * A row already at that time takes it — that is how "two at once" is written,
 * one line with two dishes — so adding never splits one moment across two
 * lines. Otherwise a new row goes in before the first row that is later, so
 * the table (and the file) read in time order.
 */
export function addEntry(rows: readonly ScheduleRow[], seconds: string, dishId: string): ScheduleRow[] {
  const existing = rows.findIndex((row) => sameTime(row.seconds, seconds))
  if (existing >= 0) {
    return rows.map((row, i) => {
      if (i !== existing) return row
      const dishes = [...row.dishes, dishId]
      return { seconds: row.seconds, dishes, line: scheduleLine(row.seconds as string, dishes) }
    })
  }
  const added: ScheduleRow = { seconds, dishes: [dishId], line: scheduleLine(seconds, [dishId]) }
  const later = rows.findIndex((row) => row.seconds !== null && Number(row.seconds) > Number(seconds))
  if (later < 0) return [...rows, added]
  return [...rows.slice(0, later), added, ...rows.slice(later)]
}

/**
 * Take one ticket off a row, or the whole row when `dishIndex` is omitted or
 * it was the row's last ticket.
 */
export function removeEntry(rows: readonly ScheduleRow[], rowIndex: number, dishIndex?: number): ScheduleRow[] {
  const row = rows[rowIndex]
  if (!row) return [...rows]
  if (dishIndex === undefined || row.seconds === null || row.dishes.length <= 1) {
    return rows.filter((_, i) => i !== rowIndex)
  }
  const dishes = row.dishes.filter((_, i) => i !== dishIndex)
  return rows.map((r, i) => (i === rowIndex ? { seconds: row.seconds, dishes, line: scheduleLine(row.seconds as string, dishes) } : r))
}

/**
 * Fold rows that share a time into the first of them, keeping ticket order —
 * what a file with `0s a` and, later, `0s b` means anyway (the engine sorts
 * by time and keeps file order within it), said on one line.
 */
export function mergeRows(rows: readonly ScheduleRow[]): ScheduleRow[] {
  const out: ScheduleRow[] = []
  for (const row of rows) {
    const into = row.seconds === null ? -1 : out.findIndex((o) => sameTime(o.seconds, row.seconds as string))
    if (into < 0) {
      out.push(row)
      continue
    }
    const target = out[into]
    const dishes = [...target.dishes, ...row.dishes]
    out[into] = { seconds: target.seconds, dishes, line: scheduleLine(target.seconds as string, dishes) }
  }
  return out
}

/** `"75"` -> `"1:15"`: a row's time the way the round clock shows it. */
export function clockTime(seconds: string): string {
  const total = Number(seconds)
  const m = Math.floor(total / 60)
  const s = total - m * 60
  const whole = Number.isInteger(s) ? String(s).padStart(2, '0') : s.toFixed(1).padStart(4, '0')
  return `${m}:${whole}`
}
